import Tilt from "react-parallax-tilt";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Wrench, MessageCircle, Settings2, ArrowUpRight } from "lucide-react";
import SectionReveal from "./SectionReveal";

const SHOP_FRONT =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/7suvjdf0_imgi_1_thumbnail_IMG_20220525_130906-2-1024x768.jpg";
const WORKSHOP =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/r465wro9_imgi_2_thumbnail_IMG_8296-3.jpg";

const SERVICES = [
  {
    icon: ShieldCheck,
    title: "MFK-Vorbereitung",
    text: "Lückenlose Vorführbereitschaft. Wir bereiten Ihre Vespa professionell auf die MFK vor — mit Erfolgsgarantie.",
    keyword: "Sicherheit",
  },
  {
    icon: Wrench,
    title: "Reparatur & Unfall",
    text: "Vom Service-Klassiker bis zur Unfallreparatur. In Kooperation mit unserem Carrosseriespengler.",
    keyword: "Werterhalt",
  },
  {
    icon: MessageCircle,
    title: "Persönliche Beratung",
    text: "Welche Vespa passt zu Ihrem Leben? Wir nehmen uns Zeit für Sie — ehrlich, kompetent, italienisch.",
    keyword: "Vertrauen",
  },
  {
    icon: Settings2,
    title: "Feineinstellung",
    text: "Vergaser, Zündung, Auspuff. Wir holen aus jedem Motor das Maximum — präzise wie ein Schweizer Uhrwerk.",
    keyword: "Präzision",
  },
];

export default function Services() {
  return (
    <section
      id="service"
      className="section relative bg-[var(--cream)]"
      data-testid="services-section"
    >
      <div className="container-wide">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-20 gap-8">
          <SectionReveal className="max-w-2xl">
            <span className="overline">Serviceleistungen</span>
            <h2 className="h-display text-5xl md:text-6xl lg:text-7xl mt-6 text-[var(--anthracite)]">
              Ihr Fahrzeug in den{" "}
              <span className="italic-accent text-[var(--sage-dark)]">Händen</span> von
              Experten.
            </h2>
          </SectionReveal>
          <SectionReveal delay={1}>
            <Link to="/casa-markus" className="btn-ghost" data-testid="services-cta-appointment">
              Termin anfragen
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </SectionReveal>
        </div>

        {/* Authentic workshop showcase */}
        <SectionReveal className="mb-20">
          <div className="grid md:grid-cols-12 gap-4 md:gap-6">
            <motion.div
              whileHover={{ y: -4 }}
              transition={{ duration: 0.5 }}
              className="md:col-span-7 relative aspect-[4/3] md:aspect-auto overflow-hidden"
              data-testid="workshop-image-front"
            >
              <img
                src={SHOP_FRONT}
                alt="Vespa Basel — Eingang Werkstatt"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s]"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 bg-gradient-to-t from-black/70 via-black/20 to-transparent text-[var(--cream)]">
                <span className="overline !text-white/80 text-[10px]">Kundendienst · Ausstellung · Verkauf</span>
                <p className="font-serif text-2xl md:text-3xl mt-2 leading-tight">
                  Wo Vespas zu Hause sind.
                </p>
              </div>
            </motion.div>
            <motion.div
              whileHover={{ y: -4 }}
              transition={{ duration: 0.5 }}
              className="md:col-span-5 relative aspect-[4/3] md:aspect-auto overflow-hidden"
              data-testid="workshop-image-interior"
            >
              <img
                src={WORKSHOP}
                alt="Werkstatt — Reparatur und Service"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s]"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 bg-gradient-to-t from-black/70 via-black/20 to-transparent text-[var(--cream)]">
                <span className="overline !text-white/80 text-[10px]">Werkstatt</span>
                <p className="font-serif text-2xl md:text-3xl mt-2 leading-tight">
                  Handwerk seit 1949.
                </p>
              </div>
            </motion.div>
          </div>
        </SectionReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <SectionReveal key={s.title} delay={i}>
                <Tilt
                  glareEnable
                  glareMaxOpacity={0.12}
                  glareColor="#7A9A8A"
                  glarePosition="all"
                  tiltMaxAngleX={6}
                  tiltMaxAngleY={6}
                  transitionSpeed={1400}
                  className="h-full"
                >
                  <div
                    className="card-clean h-full p-8 lg:p-10 flex flex-col justify-between min-h-[340px]"
                    data-testid={`service-card-${i}`}
                  >
                    <div>
                      <Icon
                        size={36}
                        strokeWidth={1.2}
                        className="text-[var(--sage-dark)]"
                      />
                      <h3 className="font-serif text-2xl md:text-3xl mt-8 text-[var(--anthracite)] leading-tight">
                        {s.title}
                      </h3>
                      <p className="mt-4 text-sm text-[var(--muted-fg)] leading-relaxed">
                        {s.text}
                      </p>
                    </div>
                    <div className="mt-10 pt-6 border-t border-[var(--border)] flex items-center justify-between">
                      <span className="overline text-[10px]">{s.keyword}</span>
                      <span className="text-[var(--sage-dark)] font-serif text-xl leading-none">
                        0{i + 1}
                      </span>
                    </div>
                  </div>
                </Tilt>
              </SectionReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
